import { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Grid,
  Stack,
  Divider
} from '@mui/material';
import { 
  Warning as WarningIcon,
  ExpandLess as ChevronUpIcon,
  ExpandMore as ChevronDownIcon
} from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { formatInt } from '../../utils/numberFormat';

/**
 * CodexHeader - Displays codex metadata and status information
 * 
 * @param {object} props
 * @param {object} props.codex - The codex data object
 * @param {object} props.statusMap - Mapping of status codes to display values
 * @param {function} props.formatDate - Function to format dates
 * @param {object} [props.selectedProcess] - Optional currently selected process
 * @param {number} [props.processedPages] - Pagine elaborate del documento (AUTO-213)
 * @param {boolean} [props.showProcessedPages] - Mostra le pagine elaborate: solo per l'admin
 */
const CodexHeader = ({
  codex,
  statusMap,
  formatDate,
  selectedProcess,
  processedPages = null,
  showProcessedPages = false,
  sx = {},
}) => {
  // State for validation messages visibility
  const [validationVisible, setValidationVisible] = useState(true);
  
  if (!codex) return null;
  
  // Get status display properties
  const statusDisplay = statusMap[codex.status] || { label: codex.status, color: 'default' };

  // Check if codex has notes (validation messages)
  const hasValidationMessages = codex.notes && codex.notes.trim().length > 0;

  return (
    <Paper sx={{ p: 3, ...sx }}>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} md={6}>
          <Stack spacing={1}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {codex.code || 'Codex'}
            </Typography>
            {codex.description && codex.description.trim() && (
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                {codex.description}
              </Typography>
            )}
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <Stack spacing={1.5} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
              <Chip
                label={statusDisplay.label || '-'}
                color={statusDisplay.color}
                variant="filled"
                size="medium"
              />
              {codex.codexType?.code && (
                <Chip label={codex.codexType.code} variant="outlined" size="medium" />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              Creato il {formatDate(codex.createdAt)} • Aggiornato il {formatDate(codex.updatedAt)}
            </Typography>
            {/*
              Pagine elaborate (AUTO-213): serve a risalire dal totale della pagina «Pagine
              elaborate» al singolo documento in caso di contestazione. Doppia guardia: il flag e il
              valore. Se il backend non popola il campo — succede per chi non è admin, dove la chiave
              non compare proprio — la riga non viene disegnata invece di mostrare «undefined».
            */}
            {showProcessedPages && Number.isFinite(processedPages) && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: { xs: 'left', md: 'right' } }}
              >
                Pagine elaborate:{' '}
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {formatInt(processedPages)}
                </Box>
              </Typography>
            )}
          </Stack>
        </Grid>
      </Grid>

      {hasValidationMessages && (
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 2 }} />
          
          {/* Validation Messages Header with Toggle */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <WarningIcon color="warning" fontSize="small" />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Validation Messages
            </Typography>
            <IconButton
              size="small"
              onClick={() => setValidationVisible(!validationVisible)}
              sx={{ ml: 'auto' }}
            >
              {validationVisible ? <ChevronUpIcon fontSize="small" /> : <ChevronDownIcon fontSize="small" />}
            </IconButton>
          </Stack>

          {/* Validation Messages Section */}
          <Box
            sx={{
              overflow: 'hidden',
              transition: 'max-height 0.3s ease, opacity 0.3s ease',
              maxHeight: validationVisible ? '500px' : '0px',
              opacity: validationVisible ? 1 : 0,
            }}
          >
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.5 }}>
              {codex.notes
                .replace(/\\n/g, '\n')
                .split('\n')
                .map((line, index, array) => (
                  <span key={index}>
                    {line}
                    {index < array.length - 1 && <br />}
                  </span>
                ))}
            </Typography>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default CodexHeader; 
